<i18n src="./resources/locales.json"></i18n>

<template>
  <div
    class="business-process-monitoring-agent"
    :inactive="!agent.isActive"
    :temporal="!!agent.expirationTime"
    @contextmenu.prevent="showContextMenu($event)"
  >
    <div class="avatar" @mouseover="openAgentStatsSummaryCard($event)" @mouseout="closeAgentStatsSummaryCard">
      <EcsAvatar :contact-id="agent.id" :size="28" stateful />
    </div>
    <div class="content">
      <div class="agent-name">{{ agent.name }}</div>
      <div class="agent-details">
        <div v-if="agentStats.isHandling">
          <v-icon small>{{ agentStats.handlingIcon }}</v-icon>
          <EcsLiveTimeAgo :since="agentStats.handlingTime" />
        </div>
        <div v-else class="inline-state-resume">
          <div class="state-name">{{ currentStatus.label }}</div>
          <EcsLiveTimeAgo v-if="currentStatus.since" :since="currentStatus.since" short-output />
        </div>
      </div>
    </div>
    <v-tooltip bottom z-index="20">
      <template v-slot:activator="{ on, attrs }">
        <v-icon v-if="!!agent.expirationTime" v-bind="attrs" small v-on="on">
          {{ temporaryAssignmentIcon }}
        </v-icon>
      </template>
      <span>{{ temporaryAssignmentDetails }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { AGENT_STATE } from '@/constants/agent/constants';
import AgentService from '@/services/agentService';
import EcsAvatar from '@/components/ecs-base-interaction-components/ecs-avatar/EcsAvatar';
import EcsLiveTimeAgo from '@/components/ecs-live-time-ago/EcsLiveTimeAgo';
import EcsState from '@/components/ecs-agent-state/EcsState';
import InteractionService from '@/services/interactionService';
import SupervisorService from '@/services/supervisorService';
import { MEDIA_TYPE_TELEPHONY } from '@/constants/socket-events';

const ERROR_MESSAGE_LABEL_KEY = 'menus.errorMessage';

export default {
  name: 'EcsBusinessProcessMonitoringAgent',

  components: { EcsLiveTimeAgo, EcsAvatar },

  props: {
    agent: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      isListen: false,
      isRecording: false,
      isCoach: false,
      isBarge: false,
      isMonitoring: false,
      actions: { start: 'start', stop: 'stop' }
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('CONTACTS', ['getAgent']),

    ...mapGetters('REFERENCES', ['availableAgentStates', 'availableCustomBreakStates']),

    ...mapGetters('SETTINGS', ['applicationSettings']),

    ...mapGetters('STATS', ['getAgentStatsById']),

    selectedAgent() {
      return this.getAgent(this.agent.id);
    },

    agentStats() {
      return this.getAgentStatsById(this.agent.id) || {};
    },

    currentStatus() {
      const customBreak = this.availableCustomBreakStates.find(breakItem => {
        return breakItem.id === this.selectedAgent.state.id;
      });

      let stateName = this.selectedAgent.state.name;

      if (customBreak) {
        stateName = stateName.replace(` (${customBreak.name})`, '');
      }

      const isOffline = EcsState.offlineStateIds.includes(this.selectedAgent.state.id);
      const isBusy = EcsState.busyStateIds.includes(this.selectedAgent.state.id);

      return {
        id: this.selectedAgent.state.id,
        label: customBreak ? `${stateName} (${customBreak.name})` : stateName,
        since: isOffline || isBusy ? null : this.agentStats.agentTimeInState.value
      };
    },

    temporaryAssignmentDetails() {
      let result = '';

      const { isActive, expirationTime } = this.agent;

      if (expirationTime) {
        const applicationTimeFormat = this.applicationSettings.timeFormat;
        let displayFormat;

        if (applicationTimeFormat === 'hh:mm A') {
          displayFormat = {
            sameDay: `[${this.$t('todayAt')}] h:mm A`,
            nextDay: `[${this.$t('dayAt')}] LL h:mm A`,
            nextWeek: `[${this.$t('dayAt')}] LL h:mm A`,
            sameElse: `[${this.$t('dayAt')}] LL h:mm A`
          };
        } else {
          displayFormat = {
            sameDay: `[${this.$t('todayAt')}] H:mm`,
            nextDay: `[${this.$t('dayAt')}] LL H:mm`,
            nextWeek: `[${this.$t('dayAt')}] LL H:mm`,
            sameElse: `[${this.$t('dayAt')}] LL H:mm`
          };
        }

        const expirationDisplayTime = moment(expirationTime)
          .utcOffset(this.applicationSettings.timeZoneUtcOffset)
          .calendar(null, displayFormat);

        result = isActive
          ? this.$t('temporaryActiveExpiration', { time: expirationDisplayTime })
          : this.$t('temporaryInactiveExpiration', { time: expirationDisplayTime });
      }

      return result;
    },

    temporaryAssignmentIcon() {
      return this.agent.isActive ? 'mdi-timer-sand' : 'mdi-timer-sand-empty';
    }
  },

  methods: {
    getInitialMenuItem() {
      return {
        label: this.$t('menus.stateMenu'),
        isDisabled: false,
        menu: this.availableAgentStates
          .filter(state => state.id !== this.selectedAgent.state.id)
          .map(item => {
            return {
              label: item.name,
              id: item.id,
              isDisabled: false,
              action: item.id !== AGENT_STATE.break ? this.changeAgentStatus : null,
              menu:
                item.id !== AGENT_STATE.break
                  ? null
                  : this.availableCustomBreakStates
                      .filter(state => state.id !== this.selectedAgent.state.id)
                      .map(i => {
                        return { label: i.name, id: i.id, action: this.changeAgentStatus, isDisabled: false };
                      })
            };
          })
      };
    },

    getSubmenuItems() {
      return [
        {
          label: this.isListen
            ? this.$t('menus.monitorSubMenus.stopListenMenu')
            : this.$t('menus.monitorSubMenus.startListenMenu'),
          id: 'listen',
          isDisabled: this.isMonitoring && !this.isListen,
          action: this.startStopListenToAgent
        },
        {
          label: this.isCoach
            ? this.$t('menus.monitorSubMenus.stopCoachMenu')
            : this.$t('menus.monitorSubMenus.startCoachMenu'),
          id: 'coach',
          isDisabled: this.isMonitoring && !this.isCoach,
          action: this.startStopCoachToAgent
        },
        {
          label: this.isBarge
            ? this.$t('menus.monitorSubMenus.stopBargeMenu')
            : this.$t('menus.monitorSubMenus.startBargeMenu'),
          id: 'bargeIn',
          isDisabled: this.isMonitoring && !this.isBarge,
          action: this.startStopBargeToAgent
        },
        {
          label: this.isRecording
            ? this.$t('menus.monitorSubMenus.stopRecordMenu')
            : this.$t('menus.monitorSubMenus.startRecordMenu'),
          id: 'recording',
          isDisabled: this.isMonitoring && !this.isRecording,
          action: this.startStopRecordToAgent
        }
      ];
    },

    showContextMenu(event) {
      const position = { x: event.clientX, y: event.clientY };
      const promises = [
        AgentService.getAgentById(this.agent.id),
        InteractionService.getAgentInteractions(this.agent.id),
        SupervisorService.getAnotherAgentLoadAllowances(this.agent.id)
      ];
      const menu = [];

      this.$root.$emit('open-context-menu', {
        menu: [
          {
            label: this.$t('menus.stateMenu'),
            isLoading: true
          },
          {
            label: this.$t('menus.allowanceMenu'),
            isLoading: true
          }
        ],
        position
      });

      Promise.all(promises).then(([agent, interactions, loadAllowances]) => {
        menu.push(this.getInitialMenuItem());

        const agentLoadAllowances = loadAllowances.data
          .filter(allowance => allowance.id !== agent.data.loadAllowanceId)
          .map(item => {
            return { label: item.name, id: item.id, action: this.changeAgentLoadAllowances, isDisabled: false };
          });
        if (agentLoadAllowances.length > 0) {
          menu.push({
            label: this.$t('menus.allowanceMenu'),
            isDisabled: false,
            menu: agentLoadAllowances
          });
        }

        if (interactions.data.length > 0) {
          const isAnInteractionTelephony = interactions.data.some(
            i => i.interactionMediaType.toUpperCase() === MEDIA_TYPE_TELEPHONY
          );
          if (isAnInteractionTelephony) {
            menu.push({
              label: this.$t('menus.monitorMenu'),
              isDisabled: this.selectedAgent.id === this.loggedInAgent.id,
              menu: this.getSubmenuItems()
            });
          }
        }
        this.$root.$emit('open-context-menu', { menu, position });
      });
    },

    changeAgentStatus(statusId) {
      SupervisorService.updateAnotherAgentState(this.agent.id, statusId).catch(() => {
        this.showSnackBar(this.$t(ERROR_MESSAGE_LABEL_KEY), 'error');
      });
    },

    changeAgentLoadAllowances(allowanceId) {
      SupervisorService.updateAnotherAgentLoadAllowance(this.agent.id, allowanceId).catch(() => {
        this.showSnackBar(this.$t(ERROR_MESSAGE_LABEL_KEY), 'error');
      });
    },

    startStopListenToAgent() {
      const action = this.isListen ? this.actions.stop : this.actions.start;
      SupervisorService.listenToAgentAction(this.agent.id, action)
        .then(() => {
          this.showSnackBar(this.$t(`menus.${action}ListenMessage`), 'success');
        })
        .catch(() => {
          this.showSnackBar(this.$t(ERROR_MESSAGE_LABEL_KEY), 'error');
        });
      this.isListen = !this.isListen;
      this.isMonitoring = !this.isMonitoring;
    },

    startStopRecordToAgent() {
      const action = this.isRecording ? this.actions.stop : this.actions.start;
      SupervisorService.recordToAgentAction(this.agent.id, action)
        .then(() => {
          this.showSnackBar(this.$t(`menus.${action}RecordMessage`), 'success');
        })
        .catch(() => {
          this.showSnackBar(this.$t(ERROR_MESSAGE_LABEL_KEY), 'error');
        });
      this.isRecording = !this.isRecording;
      this.isMonitoring = !this.isMonitoring;
    },

    startStopCoachToAgent() {
      const action = this.isCoach ? this.actions.stop : this.actions.start;
      SupervisorService.coachToAgentAction(this.agent.id, action)
        .then(() => {
          this.showSnackBar(this.$t(`menus.${action}CoachMessage`), 'success');
        })
        .catch(() => {
          this.showSnackBar(this.$t(ERROR_MESSAGE_LABEL_KEY), 'error');
        });
      this.isCoach = !this.isCoach;
      this.isMonitoring = !this.isMonitoring;
    },

    startStopBargeToAgent() {
      const action = this.isBarge ? this.actions.stop : this.actions.start;
      SupervisorService.interveneToAgentAction(this.agent.id, action)
        .then(() => {
          this.showSnackBar(this.$t(`menus.${action}BargeMessage`), 'success');
        })
        .catch(() => {
          this.showSnackBar(this.$t(ERROR_MESSAGE_LABEL_KEY), 'error');
        });
      this.isBarge = !this.isBarge;
      this.isMonitoring = !this.isMonitoring;
    },

    showSnackBar(message, type) {
      this.$root.$emit('snackbar-message', {
        message,
        type
      });
    },

    openAgentStatsSummaryCard(event) {
      this.$root.$emit('open-agent-stats-summary-card', {
        agentStats: this.agentStats,
        currentStatus: this.currentStatus,
        x: event.target.getBoundingClientRect().right,
        y: event.target.getBoundingClientRect().top
      });
    },

    closeAgentStatsSummaryCard() {
      this.$root.$emit('close-agent-stats-summary-card');
    }
  }
};
</script>

<style src="./scoped.scss" scoped lang="scss" />
